import React, { useCallback, useEffect, useState } from "react";
import { Button, Paper, Stack, Typography } from "@mui/material";
import { BUILD_ID, IS_STAMPED_BUILD } from "src/utils/buildInfo";

export const VERSION_POLL_MS = 5 * 60 * 1000;
const PAYMENT_POLL_MS = 2000;

const BUILD_TIME_RE = /\.(\d{12})$/;

/**
 * true si `remote` (public/version.json del servidor) es un build distinto y no
 * anterior al que corre. Con fecha en ambos manda la fecha (no se avisa de un
 * «nuevo» que en realidad es más viejo, p. ej. durante un rollback a medias).
 */
export function isNewerBuild(remote, own = BUILD_ID) {
  if (!remote || !own || remote === own) return false;
  const r = BUILD_TIME_RE.exec(remote);
  const o = BUILD_TIME_RE.exec(own);
  if (r && o) return r[1] > o[1];
  return true;
}

const paymentOpen = () => !!document.querySelector('[data-testid="payment-amount-due"]');

/**
 * 2026-10-05 · Aviso discreto «Hay una versión nueva · Recargar». Consulta
 * /version.json (sin caché) cada 5 min y al volver a la pestaña. Nunca fuerza
 * la recarga y se esconde mientras hay un cobro abierto. Solo en builds
 * estampados (no en `npm start`).
 */
export default function NewVersionBanner({ pollMs = VERSION_POLL_MS, fetchImpl }) {
  const [latest, setLatest] = useState(null);
  const [inPayment, setInPayment] = useState(false);

  const check = useCallback(async () => {
    try {
      const doFetch = fetchImpl || window.fetch.bind(window);
      const res = await doFetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
      if (!res || !res.ok) return;
      const json = await res.json();
      if (json && isNewerBuild(json.id)) setLatest(json.id);
    } catch {
      /* sin red o sin version.json: nada que avisar */
    }
  }, [fetchImpl]);

  useEffect(() => {
    if (!IS_STAMPED_BUILD && !fetchImpl) return undefined;
    check();
    const t = setInterval(check, pollMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [check, pollMs, fetchImpl]);

  useEffect(() => {
    if (!latest) return undefined;
    setInPayment(paymentOpen());
    const t = setInterval(() => setInPayment(paymentOpen()), PAYMENT_POLL_MS);
    return () => clearInterval(t);
  }, [latest]);

  if (!latest || inPayment) return null;

  return (
    <Paper
      data-testid="new-version-banner"
      elevation={6}
      sx={{
        position: "fixed",
        left: { xs: 10, sm: 16 },
        bottom: { xs: "calc(84px + env(safe-area-inset-bottom))", sm: 16 },
        zIndex: 1050,
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        "@media print": { display: "none" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2">Hay una versión nueva</Typography>
        <Button size="small" onClick={() => window.location.reload()}>
          Recargar
        </Button>
      </Stack>
    </Paper>
  );
}
